/**
 * PATH: packages/tenant-shared/src/services/quotechApi.ts
 * FILE_ID: TENANTSHARED-QUOTECHAPI-001
 * PURPOSE: Tenant-agnostic master Quotech API singleton (axios-style
 *          fetch wrapper) used by every tenant SPA to hit
 *          `quotech-backend.quotech.ai` against the legacy
 *          `quotech_auth` cookie.
 * WHY:     Until MR #7 this file existed as a byte-identical pair at
 *          apps/{newmark,sam}/src/shared/services/quotechApi.ts.
 *          Two clones × 16k chars = ~32k chars of duplication that
 *          drifted whenever an integration call was changed. Lifted
 *          here so both tenant shims feed off a single source.
 * RELATED:
 *   - apps/{newmark,sam}/src/shared/services/quotechApi.ts — thin
 *     re-export shims (post-MR #7).
 *   - quotech_auth cookie set by master Django backend at
 *     `quotech-backend.quotech.ai` — the source-of-truth auth
 *     credential this client bears.
 */

// Pick the master-backend base URL based on environment:
//   - Explicit VITE_QUOTECH_BACKEND wins (e.g. staging override).
//   - On localhost (vite dev server) leave it EMPTY so paths like
//     ``/api/outlook/status/`` stay relative and travel through the
//     Vite proxy in apps/sam/vite.config.ts (which forwards /api →
//     prod backend with Set-Cookie rewrite). The proxy is the only
//     way for browser fetches to talk to the prod backend without
//     tripping CORS preflight, since quotech-backend.quotech.ai
//     does not whitelist localhost origins. Pinning the absolute
//     URL here is what produced the 2026-05-09 dev-mode CORS
//     failure on /api/outlook/status/ and friends.
//   - Otherwise (production build served from dashboard.quotech.ai)
//     fall back to the absolute prod URL so calls work cross-origin
//     when the SPA is served from a CDN.
/** Node-only fallback used when `atob` is unavailable (SSR / old Node).
 *  Declared here (type-only, erased at build) so this browser package
 *  needs no @types/node — runtime behaviour is unchanged. */
declare const Buffer: { from(data: string, encoding: string): { toString(encoding: string): string } }

import {
  clearAuthV2CookieRefreshDead,
  isAuthV2CookieRefreshDead,
  markAuthV2CookieRefreshDead,
} from '../auth/authV2CookieRefreshDead'

function resolveQuotechBackend(): string {
  const explicit = import.meta.env.VITE_QUOTECH_BACKEND
  if (explicit) return explicit
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
      return ''
    }
    // Production SPAs on *.quotech.ai reach the backend through the nginx
    // same-origin /api/ proxy (see src/config/api.ts API_BASE). Using the
    // absolute quotech-backend host here caused cross-origin refresh POSTs
    // that surfaced as console 401 noise and could miss path-scoped cookies.
    if (host.endsWith('.quotech.ai')) {
      return ''
    }
  }
  return 'https://quotech-backend.quotech.ai'
}

const QUOTECH_BACKEND = resolveQuotechBackend()
const QUOTECH_AUTH_COOKIE = 'quotech_auth'

/** Mirrors src/config/authv2.ts — tenant-shared cannot import master config. */
function isAuthV2Enabled(): boolean {
  return (
    String((import.meta.env as Record<string, string | undefined>)?.VITE_QUOTECH_AUTHV2 || '')
      .toLowerCase()
      .trim() === 'true'
  )
}

const V1_REFRESH_ENDPOINT = '/api/auth/refresh-token/'

function isAuthLandingPath(pathname: string): boolean {
  const normalised = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return normalised === '' || normalised === '/' || normalised === '/login' || normalised === '/sso'
}

function getV1RefreshToken(): string | null {
  if (typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem('refreshToken')
  } catch {
    return null
  }
}

function buildBackendUrl(path: string): string {
  if (!QUOTECH_BACKEND) return path
  return `${QUOTECH_BACKEND.replace(/\/+$/, '')}${path}`
}

/** Persist a v1-refreshed access token for quotechFetch (reads quotech_auth). */
function persistV1AccessToken(access: string): void {
  try {
    localStorage.setItem('IdToken', access)
    localStorage.setItem('accessTokennew', access)
  } catch {
    /* private mode / quota */
  }
  if (typeof document === 'undefined') return
  try {
    const domain = window.location.hostname.includes('quotech.ai') ? '; domain=.quotech.ai' : ''
    const secure = window.location.protocol === 'https:' ? '; secure' : ''
    const remember = document.cookie.includes('quotech_remember_me=1')
    const expires = remember
      ? `; expires=${new Date(Date.now() + 30 * 864e5).toUTCString()}`
      : ''
    document.cookie =
      `${QUOTECH_AUTH_COOKIE}=${encodeURIComponent(access)}; path=/${domain}${expires}${secure}; samesite=lax`
  } catch {
    /* ignore cookie-write failure */
  }
}

/**
 * Build-time override for the master Quotech `Organisation.id` this
 * SPA represents.
 *
 * Why we need it: the shared SAM-API session payload returns
 * `organisation.id` as the LOCAL sam-api id (e.g. Newmark = 20). The
 * master Django RBAC gate keys on the MASTER Quotech id (Newmark = 6).
 * Without this override, every cross-master call from a non-superuser
 * tenant 403s with "You do not have access to the Email Hub." (and the
 * same for Kanban / Outlook / Gmail) — because RBAC checks
 * `organisation_id=20`, where Mark has zero permissions, instead of
 * `organisation_id=6` where he is org_owner.
 *
 * Set via `VITE_QUOTECH_ORG_ID` per tenant build. Empty / unset → fall
 * back to the runtime value pushed in by the session (preferred), and
 * finally to the explicit `tenantId` arg (legacy behaviour for SAM,
 * which historically only had superusers and so the wrong id was
 * harmless).
 *
 * Tracked as BUG-0010 in docs/bugs/BUG_LEDGER.md.
 */
const QUOTECH_ORG_ID_OVERRIDE: number | null = (() => {
  const raw = (import.meta.env as Record<string, string | undefined>)
    ?.VITE_QUOTECH_ORG_ID
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
})()

export function getQuotechOrgIdOverride(): number | null {
  return QUOTECH_ORG_ID_OVERRIDE
}

/**
 * Runtime master Quotech org id sourced from
 * `session.organisation.quotech_org_id` (sam-api emits this as of
 * the BUG-0010 permanent fix on the BE — see MR !2749).
 *
 * Resolution priority for the cross-master `?tenant=` query param:
 *
 *   1. **Runtime session id** (this slot)  — set on every successful
 *      `/auth/sso` and `/auth/me` round-trip via
 *      `setRuntimeQuotechOrgId`. This is the canonical answer; it is
 *      tied to the actually-signed-in tenant and survives multi-org
 *      switching from a single bundle. The FE must NEVER second-guess
 *      it: if the session says `quotech_org_id=6`, that's the one we
 *      use.
 *   2. **Build-time override** (`VITE_QUOTECH_ORG_ID`) — fallback for
 *      legacy tenants whose `OrganisationSettings.quotech_org_id`
 *      predates the wiring (still emits `null` on the wire). Also
 *      what early Newmark/SAM bundles relied on before the BE perm
 *      fix landed. Safe to delete from `.env.production` once the
 *      session-driven path is verified for every tenant in this
 *      bundle.
 *   3. **Explicit `tenantId` arg** — last resort; used by callers
 *      that already know the right id (e.g. server-rendered pages,
 *      tests).
 *
 * Centralising the priority here means callers can keep using
 * `quotechFetch(..., { includeTenant: true })` without knowing
 * which seam they're crossing.
 *
 * See `id-identity-at-every-boundary` in
 * `docs/bugs/ROBUSTNESS_PRINCIPLES.md`.
 */
let runtimeQuotechOrgId: number | null = null

/**
 * Push a master Quotech org id into the runtime store. Called by the
 * `applySessionSideEffect` hook in `ApiAuthGate.tsx` whenever a fresh
 * session payload arrives. Treats anything non-positive-integer as
 * "no value" so a future BE bug can't poison the FE.
 */
export function setRuntimeQuotechOrgId(value: number | null | undefined): void {
  if (value == null) {
    runtimeQuotechOrgId = null
    return
  }
  const n = Number(value)
  runtimeQuotechOrgId = Number.isFinite(n) && n > 0 ? n : null
}

export function getRuntimeQuotechOrgId(): number | null {
  return runtimeQuotechOrgId
}

/**
 * Resolve the effective `tenant=` value for a cross-master call,
 * applying the priority chain above. Exported so tests + callers
 * that need the value (e.g. building a manual URL) don't have to
 * re-implement the rules.
 */
export function resolveQuotechTenantId(explicitTenantId?: number | null): number | null {
  if (runtimeQuotechOrgId != null) return runtimeQuotechOrgId
  if (QUOTECH_ORG_ID_OVERRIDE != null) return QUOTECH_ORG_ID_OVERRIDE
  if (explicitTenantId != null) {
    const n = Number(explicitTenantId)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  return null
}

/**
 * Custom event dispatched whenever a master-backend call (emails,
 * kanban, outlook, etc.) returns 401. Consumed by the global
 * QuotechReconnectBanner mounted in AppShell — it surfaces a sticky
 * "Reconnect Quotech" pill instead of letting calls silently fail.
 *
 * Why this matters: SAM has TWO auth systems running side-by-side:
 *   1. steel-api (sam-api) — Bearer + HttpOnly refresh cookie.
 *   2. Django master backend — legacy ``quotech_auth`` Bearer cookie
 *      (non-HttpOnly, set by the master Quotech SPA on .quotech.ai).
 *
 * If a user logs out, signs back in via just the SAM refresh cookie,
 * or has the legacy ``quotech_auth`` cookie expire / get cleared while
 * the SAM steel-api session is still valid, EVERY master-backend call
 * 401s — but the SAM SPA itself stays "logged in", so the user sees
 * an authenticated dashboard with broken Email Hub / Kanban / Outlook
 * status. That was exactly the symptom reported on 2026-05-02 with
 * a string of 401s on /api/emails/ and /api/kanban/boards/.
 *
 * The recovery: dispatch QUOTECH_AUTH_LOST_EVENT on the FIRST 401,
 * have the global banner read the event, and offer a one-click
 * redirect to the master Quotech login (which re-mints the cookie
 * and bounces the user back).
 */
export const QUOTECH_AUTH_LOST_EVENT = 'quotech:auth-lost'

/**
 * Companion to `QUOTECH_AUTH_LOST_EVENT`. Dispatched the moment the
 * master session recovers — either a silent refresh succeeded, or a
 * plain master call came back 2xx after we'd previously surfaced the
 * "connection lost" banner (e.g. the operator reconnected in a sibling
 * tab, or a transient blip cleared). The global `QuotechReconnectBanner`
 * listens for this and auto-hides.
 *
 * Why this matters (BUG-2026-05-31-RECONNECT-BANNER-STICKY): the banner
 * was sticky — once `QUOTECH_AUTH_LOST_EVENT` fired it stayed visible
 * until the operator manually clicked Reconnect or dismissed it. A
 * SINGLE transient refresh failure (a 504 / network blip on the 12s
 * board `/version/` poll) trips the 30s refresh backoff, so the next
 * few poll 401s fall through to the banner — which then lied
 * "Email Hub, Kanban, and Outlook are signed out" while every feature
 * was actually loading fine, because the very next poll succeeded.
 * Emitting a restored signal on the first post-loss success closes
 * that gap without weakening the genuine-signed-out path (where no
 * master call ever succeeds until the user reconnects).
 */
export const QUOTECH_AUTH_RESTORED_EVENT = 'quotech:auth-restored'

/**
 * Latch: have we surfaced an auth-lost banner that a subsequent
 * successful master call hasn't yet cleared? Gates
 * `dispatchQuotechAuthRestored` so the restored event fires exactly
 * once per recovery, NOT on every routine hourly silent refresh (which
 * happens transparently and must not toggle any UI).
 */
let authLostActive = false

export interface QuotechRequestOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | null | Record<string, unknown>
  includeTenant?: boolean
  tenantId?: number | null
  /** BUG-2026-05-23-EMAIL-STUCK-LOADING: per-request override of the
   *  default client-side fetch timeout. Set to 0 (or any non-positive
   *  number) to OPT OUT of the timeout for a specific call — useful
   *  for long-poll endpoints or streamed responses where waiting
   *  indefinitely is the intended contract. Default (when undefined)
   *  is QUOTECH_DEFAULT_FETCH_TIMEOUT_MS. */
  timeoutMs?: number
  /** BUG-2026-06-10-VIEWPREF-IDENTITY-RELOAD-LOOP: opt OUT of the
   *  server-asserted identity check (`_assertResponseIdentityLocal`)
   *  for this call. The identity check schedules a full-page reload
   *  when the response's `X-User-Id` differs from the per-tab pin — a
   *  hard, tab-wide side effect that is the RIGHT response for a
   *  data-bearing call (it stops a sibling-tab cookie swap from leaking
   *  another user's emails/jobs into this tab) but the WRONG response
   *  for a COSMETIC, fail-soft call. The view-preference engine
   *  (`dbViewPreference.ts`) documents itself as fail-soft, yet a
   *  preference GET firing during a benign cookie/bearer identity drift
   *  (e.g. master dashboard: localStorage bearer = user 19, `quotech_auth`
   *  cookie = user 18) triggered a reload that can never converge (axios
   *  re-pins to the valid bearer each load) → strobe until the breaker
   *  trips. Set `true` ONLY for cosmetic, non-data reads/writes that must
   *  never seize the tab; the genuine drift is still surfaced by every
   *  data-bearing call AND the IdentityMismatchBanner. Default (undefined
   *  / false) preserves the security guard. */
  skipIdentityAssert?: boolean
}

/** BUG-2026-05-23-EMAIL-STUCK-LOADING: default client-side fetch
 *  timeout for quotechFetch. Set to 25s — long enough that the BE's
 *  slowest legitimate endpoints (initial inbox sync, large kanban
 *  board hydration) succeed, short enough that a stalled connection
 *  (proxy reaping, TCP half-close, hung silent-refresh) doesn't pin
 *  the calling UI on a perpetual spinner.
 *
 *  Why 25s and not 30s: the BE's nginx + uwsgi stack has a 30s server
 *  read timeout on most endpoints, so anything that ACTUALLY hits the
 *  BE will either respond or 504-fail well before 25s. A request that
 *  hangs past 25s is structurally a client-side stall (DNS, TLS, proxy,
 *  silent-refresh dedup), not a slow backend. Cutting at 25s gives
 *  the UI a clean abort + retry surface before nginx sends a 504. */
export const QUOTECH_DEFAULT_FETCH_TIMEOUT_MS = 25_000

/** BUG-2026-05-23-EMAIL-STUCK-LOADING: tighter timeout for the silent-
 *  refresh endpoint specifically. Refresh is supposed to be sub-second
 *  (a single DB read + cookie write), so 10s is already extreme. If
 *  it takes longer the BE is broken — fail closed so the banner can
 *  surface instead of all 401-retry callers waiting on the same
 *  in-flight refresh forever. */
export const QUOTECH_REFRESH_FETCH_TIMEOUT_MS = 10_000

function getQuotechToken(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${QUOTECH_AUTH_COOKIE}=([^;]*)`))
  const cookieToken = match ? decodeURIComponent(match[1]) : null

  // BUG-2026-07-03-QUOTECHFETCH-COOKIE-ONLY-BEARER (STALE-COOKIE-JAR family,
  // residual (b) of BUG-2026-06-26-COOKIE-BEARER-NOT-ANCHORED): some sessions
  // keep their live bearer ONLY in localStorage (`IdToken`) while the
  // JS-readable `quotech_auth` cookie has lapsed or was never seeded on this
  // origin. Reading ONLY the cookie meant boot-time callers (email-hub's
  // hidden-mailboxes preference hydration) fired with NO Authorization header
  // → guaranteed 401. Family invariant: every credential reader must resolve
  // the bearer from ALL the places it can live and prefer a NON-expired one.
  if (cookieToken && !isJwtExpiredLocal(cookieToken)) return cookieToken
  const stored = readLocalStorageBearerLocal()
  if (stored && !isJwtExpiredLocal(stored)) return stored
  // Nothing live anywhere — preserve legacy behaviour (return the cookie,
  // possibly expired/null) so the 401 → silent-refresh path still engages.
  return cookieToken
}

/** localStorage bearer candidates, same precedence as
 *  `SessionManager.readLiveBearer` (packages/auth). Kept local because
 *  tenant-shared deliberately does not depend on @quotech/auth. */
function readLocalStorageBearerLocal(): string | null {
  if (typeof localStorage === 'undefined') return null
  try {
    return (
      localStorage.getItem('accessTokennew')
      || localStorage.getItem('IdToken')
      || localStorage.getItem('accessToken')
    )
  } catch {
    return null
  }
}

/** True when the JWT is malformed or its `exp` is in the past or within
 *  30s of it (skew allowance — a token about to die is not worth
 *  preferring over a live fallback). A token without `exp` is treated
 *  as usable — expiry is the server's call on such tokens. */
function isJwtExpiredLocal(token: string): boolean {
  const payload = decodeJwtPayloadLocal(token)
  if (!payload) return true
  const exp = payload.exp
  if (typeof exp !== 'number') return false
  return exp * 1000 <= Date.now() + 30_000
}

// Throttle: don't fire the auth-lost event more than once every
// 30s — a single page load may queue 6+ simultaneous master-backend
// calls (emails, stats, outlook status, kanban boards, …) and we
// don't want to spam the banner / re-auth UI with one event per call.
let lastAuthLostDispatchedAt = 0

/**
 * Public throttled dispatcher for `QUOTECH_AUTH_LOST_EVENT`. Exists so
 * other master-backend HTTP wrappers (e.g. `@quotech/kanban-host`'s
 * `createKanbanFetchApi` — which doesn't route through `quotechFetch`)
 * can fire the same event WITHOUT duplicating the 30s throttle or
 * re-implementing the cookie check.
 *
 * Throttle scope: process-wide (module singleton). Both `quotechFetch`
 * and any caller of this function share the same 30s window, so a 401
 * from `/api/kanban/boards/` followed 100ms later by a 401 from
 * `/api/outlook/status/` produces ONE banner, not two — matches the
 * existing UX contract.
 *
 * Safe to call from SSR / test contexts: every `window` access is
 * guarded; missing `window` returns silently.
 *
 * NOTE: in normal operation `quotechFetch` will FIRST try
 * `attemptSilentRefresh()` on a 401 and only dispatch this event if
 * the silent refresh fails. This dispatcher remains exported so
 * callers that bypass `quotechFetch` (e.g. the kanban fetch path)
 * can still trigger the banner as a fallback, but it should now
 * fire much more rarely (only when the 30-day refresh cookie is
 * also gone — i.e. the user genuinely needs to re-login).
 */
export function dispatchQuotechAuthLost(path: string): void {
  if (typeof window === 'undefined') return
  const now = Date.now()
  if (now - lastAuthLostDispatchedAt < 30_000) return
  lastAuthLostDispatchedAt = now
  authLostActive = true
  try {
    window.dispatchEvent(
      new CustomEvent(QUOTECH_AUTH_LOST_EVENT, {
        detail: { path, hadCookie: !!getQuotechToken(), at: now },
      }),
    )
  } catch { /* ignore — old browsers without CustomEvent ctor */ }
}

/**
 * Signal that the master session has recovered. No-op unless an
 * auth-lost banner is currently active (so routine hourly refreshes
 * don't spam the event). Resets the auth-lost throttle so the NEXT
 * genuine drop surfaces immediately instead of being swallowed by the
 * 30s window left over from the recovered loss.
 *
 * Exported so master-backend HTTP wrappers that don't route through
 * `quotechFetch` (the kanban fetch path) can announce recovery with
 * the same semantics.
 */
export function dispatchQuotechAuthRestored(): void {
  if (typeof window === 'undefined') return
  if (!authLostActive) return
  authLostActive = false
  lastAuthLostDispatchedAt = 0
  try {
    window.dispatchEvent(
      new CustomEvent(QUOTECH_AUTH_RESTORED_EVENT, { detail: { at: Date.now() } }),
    )
  } catch { /* ignore — old browsers without CustomEvent ctor */ }
}

function maybeDispatchAuthLost(path: string) {
  dispatchQuotechAuthLost(path)
}

// =============================================================
// SILENT TOKEN REFRESH
// =============================================================
//
// The 401-cookie-expired path used to surface a "Reconnect Quotech"
// banner the operator had to click. That's broken UX — the BE has
// already shipped `POST /api/auth/v2/refresh/` which reads the
// 30-day `quotech_refresh` HttpOnly cookie and mints a fresh access
// cookie pair (`quotech_access` HttpOnly + `quotech_auth` JS-readable
// legacy mirror) on the same response. So 99% of "access cookie
// expired" cases can be silently fixed in one round-trip — no UI
// prompt, no manual click, no full-page navigation.
//
// Contract:
//   - Returns `true` if the refresh round-trip succeeded (cookies
//     are now fresh and any subsequent quotechFetch will pick them
//     up automatically via document.cookie + credentials:'include').
//   - Returns `false` if the refresh round-trip failed (network
//     error, 401 from refresh endpoint = refresh cookie also
//     missing/expired/revoked, or any other failure). The caller
//     should then fall through to whatever UX it shows for true
//     auth loss (typically the QuotechReconnectBanner).
//
// Concurrency: a single page load may produce 5-10 parallel 401s
// (email-accounts + outlook/status + gmail/status + kanban/boards +
// diagnostics + ...). We MUST collapse all of them onto ONE refresh
// round-trip — otherwise the BE sees a thundering herd of refresh
// calls all racing to mint cookies, and the FE wastes ~5 round-trips
// to do work that ONE call would solve. We achieve this via a
// module-singleton `_inflightRefresh` promise: the first 401 starts
// the refresh; every subsequent 401 within the same window awaits
// the same promise.
//
// Backoff: if a refresh attempt fails, we lock out further attempts
// for 30s. Without this, a refresh cookie that has been definitively
// revoked would produce a refresh-storm on every subsequent 401 (the
// user lands on a page that fires 10 parallel calls, each tries
// refresh, each fails, and we DDoS our own auth endpoint).
//
// Why 30s: matches the auth-lost-event throttle so the operator
// sees ONE banner per genuine logout, not a cascade.

let _inflightRefresh: Promise<boolean> | null = null
let _lastRefreshFailedAt = 0
const REFRESH_FAILURE_BACKOFF_MS = 30_000

// Path is parameterised so tests can override + so a future BE
// migration can point at a different endpoint without a fan-out
// search. Default matches the live `authentication.authv2.urls`
// registration.
const REFRESH_ENDPOINT = '/api/auth/v2/refresh/'

/**
 * Silently refresh the `quotech_auth` / `quotech_access` cookie pair
 * by POSTing to the master Django `quotech_refresh` refresh endpoint.
 * See module comment above for the full contract.
 *
 * Exported so other master-backend HTTP wrappers (kanban fetch,
 * future workforce / qdrive fetch) can call the same primitive
 * without re-implementing the dedup + backoff logic.
 */
export async function attemptSilentRefresh(): Promise<boolean> {
  if (typeof window === 'undefined') return false

  // On auth landing pages, skip refresh when there is no v1 refresh material.
  // HttpOnly v2 cookies are invisible to JS, so v2 mode still attempts refresh.
  if (isAuthLandingPath(window.location.pathname) && !isAuthV2Enabled() && !getV1RefreshToken()) {
    return false
  }

  // BUG-2026-08-03-AUTHV2-COOKIE-REFRESH-401-STORM: durable latch across
  // SessionManager / axios / this client — HttpOnly cookie cannot use the
  // v1 token tombstone. Stronger than the 30s backoff below.
  if (isAuthV2Enabled() && isAuthV2CookieRefreshDead()) {
    return false
  }

  // Hard backoff after a known failure. Critical for the "refresh
  // cookie is dead" case — without this, every subsequent 401 would
  // hammer the refresh endpoint until the operator manually
  // navigates away.
  if (Date.now() - _lastRefreshFailedAt < REFRESH_FAILURE_BACKOFF_MS) {
    return false
  }
  // Dedupe parallel attempts. Every 401 that arrives while a
  // refresh is in flight will await the same promise.
  if (_inflightRefresh) return _inflightRefresh

  // Capture the previous access-token `sub` BEFORE we refresh. The
  // BE's `_set_auth_cookies` will overwrite `quotech_auth` on the
  // response; if the new cookie's `sub` differs, a sibling tab
  // logged in as a different user and Tab A would silently start
  // making requests under the new identity. See
  // BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT.
  const previousSub = decodeJwtSubLocal(getQuotechToken())

  _inflightRefresh = (async () => {
    // BUG-2026-05-23-EMAIL-STUCK-LOADING: tight client-side timeout
    // on the silent-refresh round-trip. Refresh is supposed to be
    // sub-second (one DB read + cookie write). When it hangs (proxy
    // reaping, BE worker stuck, network half-close), EVERY 401-retry
    // caller awaits the same `_inflightRefresh` promise — the dedup
    // becomes a poison-pill that pins email/kanban/bills/invoices
    // fetches on a perpetual spinner. Failing closed at 10s lets the
    // banner surface and gives the UI a clean retry path.
    const refreshTimeoutController = new AbortController()
    const refreshTimeoutHandle = setTimeout(() => {
      refreshTimeoutController.abort()
    }, QUOTECH_REFRESH_FETCH_TIMEOUT_MS)
    try {
      const v1RefreshToken = getV1RefreshToken()
      const refreshUrl = isAuthV2Enabled()
        ? buildBackendUrl(REFRESH_ENDPOINT)
        : v1RefreshToken
          ? buildBackendUrl(V1_REFRESH_ENDPOINT)
          : null

      if (!refreshUrl) {
        return false
      }

      const resp = await fetch(refreshUrl, {
        method: 'POST',
        // credentials:'include' sends HttpOnly v2 refresh cookies when
        // AUTHV2 is on. Harmless for v1 (body carries the token).
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: isAuthV2Enabled()
          ? '{}'
          : JSON.stringify({ refresh: v1RefreshToken }),
        signal: refreshTimeoutController.signal,
      })
      if (resp.ok) {
        if (!isAuthV2Enabled()) {
          const data = await resp.json().catch(() => null)
          const newToken = data?.access || data?.access_token
          if (newToken) {
            persistV1AccessToken(newToken)
          }
        }
        // Cross-tab identity guard. The BE just overwrote
        // `quotech_auth`; read it back and compare to `previousSub`.
        // Mismatch ⇒ refuse to treat the refresh as successful and
        // hard-reload so the new identity is rebooted cleanly.
        const newSub = decodeJwtSubLocal(getQuotechToken())
        if (previousSub && newSub && previousSub !== newSub) {
          // BUG-2026-05-22-EVERY-SECOND-RELOAD-LOOP (fifth manifestation):
          //   Same cleanup as the response-header mismatch path
          //   below. Without this, sessionStorage tab pin survives
          //   the reload and the post-reload first response trips
          //   `_assertResponseIdentityLocal` which reloads again.
          try {
            localStorage.removeItem('IdToken')
            localStorage.removeItem('accessTokennew')
            localStorage.removeItem('access_token')
            localStorage.removeItem('refreshToken')
          } catch { /* private mode / quota — ignore */ }
          try {
            sessionStorage.removeItem('quotech_tab_user_id')
          } catch { /* private mode / quota — ignore */ }
          console.warn(
            '[quotechApi] Silent refresh returned a token for a different ' +
            `user — previousSub='${previousSub}' newSub='${newSub}'. ` +
            'Cleared stale bearer + tab pin; reloading.',
          )
          // Don't broadcast — the writing tab already broadcasts on its
          // setAuth/applySession path. Just reload this tab; the
          // identity-change receiver will fire on subsequent cookie writes.
          if (typeof window !== 'undefined') {
            setTimeout(() => { window.location.reload() }, 150)
          }
          return false
        }
        // The BE's _set_auth_cookies has now re-set all three
        // cookies on the response (quotech_access HttpOnly +
        // quotech_refresh HttpOnly + quotech_auth JS-readable
        // legacy mirror). The next quotechFetch will pick them up
        // via document.cookie and credentials:'include' — no
        // further work needed here.
        //
        // Clear any lingering failure backoff: a prior transient
        // failure (network blip / 5xx) must not keep the NEXT 401
        // locked out of refreshing now that we know the session is
        // healthy again. Then announce recovery so the sticky
        // reconnect banner auto-hides (BUG-2026-05-31-RECONNECT-
        // BANNER-STICKY). `dispatchQuotechAuthRestored` no-ops unless
        // a banner is actually active, so routine hourly refreshes
        // stay silent.
        _lastRefreshFailedAt = 0
        if (isAuthV2Enabled()) clearAuthV2CookieRefreshDead()
        dispatchQuotechAuthRestored()
        return true
      }
      _lastRefreshFailedAt = Date.now()
      if (isAuthV2Enabled() && (resp.status === 401 || resp.status === 403)) {
        markAuthV2CookieRefreshDead()
      }
      return false
    } catch {
      // Network error, CORS rejection, DNS, or our own 10s timeout.
      // Treat as a refresh failure for backoff purposes — better to
      // show the banner once than to silently retry-storm. The
      // timeout path lands here naturally because aborting the
      // refresh fetch throws an AbortError from the await above.
      _lastRefreshFailedAt = Date.now()
      return false
    } finally {
      // BUG-2026-05-23-EMAIL-STUCK-LOADING: clear the timeout on EVERY
      // exit path. Without this, an early-return from the ok-path
      // leaves a setTimeout reference alive for the remainder of the
      // 10s window; harmless functionally but leaks a closure that
      // can interfere with React fast-refresh in dev.
      clearTimeout(refreshTimeoutHandle)
      _inflightRefresh = null
    }
  })()
  return _inflightRefresh
}

/** Local JWT-sub decoder for the silent-refresh identity guard. Kept
 *  inline (not imported from @quotech/auth) because tenant-shared
 *  has historically been a leaf package; introducing the dep
 *  is a separate decision tracked in
 *  BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT follow-ups. Mirrors the
 *  `decodeJwtSub` implementation in
 *  `packages/auth/src/session-manager/tokenSync.ts`. */
function decodeJwtSubLocal(token: string | null | undefined): string | null {
  const json = decodeJwtPayloadLocal(token)
  if (!json) return null
  const sub = json.sub
  if (sub === null || sub === undefined) return null
  return String(sub).trim().toLowerCase()
}

/** Shared base64url payload decoder for the local JWT helpers
 *  (`decodeJwtSubLocal`, `isJwtExpiredLocal`). Returns null on any
 *  malformed input. */
function decodeJwtPayloadLocal(token: string | null | undefined): Record<string, unknown> | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const middle = parts[1]
  if (!middle) return null
  try {
    const padded = middle.replace(/-/g, '+').replace(/_/g, '/')
    const pad = padded.length % 4
    const b64 = pad ? padded + '='.repeat(4 - pad) : padded
    const decoded =
      typeof atob !== 'undefined'
        ? atob(b64)
        : Buffer.from(b64, 'base64').toString('binary')
    const json = JSON.parse(decoded) as Record<string, unknown>
    if (!json || typeof json !== 'object') return null
    return json
  } catch {
    return null
  }
}

/** Test seam: reset the silent-refresh module state between tests.
 *  NOT exported from the package barrel — only consumed by the
 *  matching test file. */
export function __resetSilentRefreshStateForTests(): void {
  _inflightRefresh = null
  _lastRefreshFailedAt = 0
  lastAuthLostDispatchedAt = 0
  authLostActive = false
  clearAuthV2CookieRefreshDead()
}

function buildQuotechUrl(path: string, tenantId?: number | null, includeTenant = false): string {
  // Resolve the effective `tenant=` value via the priority chain
  // (session > build-time override > explicit arg). The session-driven
  // path is the canonical answer once the BE BUG-0010 perm fix ships
  // (sam-api MR α — !2749); the env-var override stays as a defence in
  // depth so a stale or pre-perm-fix bundle still works. See
  // `resolveQuotechTenantId` for the rule + BUG-0010 in the ledger.
  const effectiveTenantId = includeTenant
    ? resolveQuotechTenantId(tenantId)
    : null

  // Empty QUOTECH_BACKEND in localhost dev means "use same-origin so
  // the Vite proxy can intercept". new URL(path, '') throws because
  // an empty string isn't a valid base — fall back to manual query
  // string handling for that case.
  if (!QUOTECH_BACKEND) {
    if (!includeTenant || !effectiveTenantId) return path
    const sep = path.includes('?') ? '&' : '?'
    const tenantPattern = /[?&]tenant=/
    if (tenantPattern.test(path)) return path
    return `${path}${sep}tenant=${encodeURIComponent(String(effectiveTenantId))}`
  }
  const url = new URL(path, QUOTECH_BACKEND)
  if (includeTenant && effectiveTenantId) {
    if (!url.searchParams.get('tenant')) {
      url.searchParams.set('tenant', String(effectiveTenantId))
    }
  }
  return url.toString()
}

function normalizeBody(body: QuotechRequestOptions['body'], headers: Headers): BodyInit | null | undefined {
  if (body == null) return body
  if (body instanceof FormData) return body
  if (typeof body === 'string') {
    if (!headers.has('Content-Type') && (body.startsWith('{') || body.startsWith('['))) {
      headers.set('Content-Type', 'application/json')
    }
    return body
  }
  if (body instanceof Blob || body instanceof ArrayBuffer || body instanceof URLSearchParams) {
    return body
  }
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return JSON.stringify(body)
}

/**
 * Inner one-shot fetch. Used by `quotechFetch` directly AND by the
 * 401-retry path (which calls this a second time after a successful
 * silent refresh). Kept separate from `quotechFetch` so the retry
 * doesn't recurse through the 401 handler.
 */
async function _quotechFetchOnce(
  path: string,
  options: QuotechRequestOptions,
): Promise<Response> {
  const {
    tenantId,
    includeTenant = false,
    body,
    headers: rawHeaders,
    timeoutMs,
    skipIdentityAssert = false,
    signal: callerSignal,
    ...init
  } = options
  const headers = new Headers(rawHeaders)
  const token = getQuotechToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  // FE↔BE seam correlation (mirrors @quotech/api axios client): stamp the
  // in-flight action's id as X-Correlation-ID on WRITE requests. The id is
  // read from the window.__QT_OBS__ mirror published by @quotech/api's
  // observability module (runWithActionId / the TanStack seam tap) — a
  // window bridge, not a package import, to keep dependency direction clean.
  const method = (init.method ?? 'GET').toUpperCase()
  if (method !== 'GET' && method !== 'HEAD' && !headers.has('X-Correlation-ID')) {
    const obsActionId =
      typeof window !== 'undefined'
        ? (window as { __QT_OBS__?: { currentActionId?: string | null } })
            .__QT_OBS__?.currentActionId
        : null
    if (obsActionId) headers.set('X-Correlation-ID', obsActionId)
  }
  const normalizedBody = normalizeBody(body, headers)

  // BUG-2026-05-23-EMAIL-STUCK-LOADING: client-side fetch timeout.
  // Native fetch() has no default timeout — a TCP-stalled connection,
  // a hung silent-refresh, or a half-closed proxy connection means
  // the promise NEVER resolves. The calling UI (Email Hub's
  // useEmails, Kanban's useBoards, Bills' useBills, …) treats
  // "loading=true and the fetch hasn't returned" as a perpetual
  // spinner. Operator 2026-05-23 hit this on Newmark Email Hub
  // after the master ↔ tenant login bounce loop (qf!3332 / BUG-
  // 2026-05-23-LOGIN-BOUNCE-LOOP) recovered: the post-recovery
  // /api/emails/?folder=inbox request hung at the client side
  // ("Loading…" spinner with no error, ever).
  //
  // Compose the caller's signal (if any) with a timeout signal so
  // EITHER source can abort the underlying fetch. The composite
  // shape means:
  //   - filter-change cancel paths (useEmails passes its own signal)
  //     still work — caller abort takes priority.
  //   - a hung request that the caller forgot to cancel STILL aborts
  //     after `timeoutMs`, surfacing a typed `TimeoutError` so the
  //     UI can render a retry surface.
  //   - opt-out (`timeoutMs <= 0`) is preserved for streaming /
  //     long-poll callers that intentionally wait indefinitely.
  const effectiveTimeoutMs = timeoutMs ?? QUOTECH_DEFAULT_FETCH_TIMEOUT_MS
  const timeoutEnabled = effectiveTimeoutMs > 0
  const timeoutController = timeoutEnabled ? new AbortController() : null
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null
  if (timeoutController) {
    timeoutHandle = setTimeout(() => {
      timeoutController.abort()
    }, effectiveTimeoutMs)
  }
  const combinedSignal = _composeAbortSignals(callerSignal, timeoutController?.signal)

  let response: Response
  try {
    response = await fetch(buildQuotechUrl(path, tenantId, includeTenant), {
      ...init,
      headers,
      body: normalizedBody,
      credentials: 'include',
      signal: combinedSignal,
    })
  } catch (err) {
    // Distinguish three abort sources so the UI layer can act
    // accordingly:
    //   1. Caller aborted (filter-change cancel) — re-throw as a
    //      standard AbortError; callers already handle this as a
    //      no-op (see useEmails.fetchEmails catch branch).
    //   2. Timeout aborted (our composite signal) — throw a typed
    //      TimeoutError so the UI can render a "Request timed out
    //      — Retry" surface instead of a generic "fetch aborted"
    //      no-op.
    //   3. Network error (DNS, TCP reset, TLS handshake) — re-throw
    //      verbatim; quotechJson translates these to a
    //      QuotechApiError with status=0.
    const callerAborted = callerSignal?.aborted === true
    const timeoutAborted = timeoutController?.signal.aborted === true && !callerAborted
    if (timeoutAborted) {
      throw new QuotechFetchTimeoutError(path, effectiveTimeoutMs)
    }
    throw err
  } finally {
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle)
    }
  }

  // Server-asserted identity check (BUG-2026-05-21 Option 3). Every
  // authenticated response from quotech-backend.quotech.ai carries
  // `X-User-Id` (stamped by IdentityResponseHeaderMiddleware). Compare
  // it to the per-tab sessionStorage pin; on a mismatch the cookie
  // jar got swapped under us by a sibling tab → reload after 150 ms.
  //
  // Inlined here (rather than calling @quotech/auth.handleResponseIdentity)
  // to mirror `decodeJwtSubLocal`'s precedent in this same file —
  // tenant-shared deliberately does not depend on @quotech/auth, so
  // the check is hand-rolled. Behaviour MUST stay byte-equivalent to
  // packages/auth/src/session-manager/tabIdentity.ts; the source-pin
  // test `crossTabIdentity.pin.test.ts` enforces the symmetry.
  //
  // BUG-2026-06-10-VIEWPREF-IDENTITY-RELOAD-LOOP: cosmetic, fail-soft
  // callers (the view-preference engine) opt out so a benign identity
  // drift can never seize the tab into a non-converging reload loop.
  // The guard still runs for every data-bearing call.
  if (!skipIdentityAssert) {
    try { _assertResponseIdentityLocal(response, path) } catch { /* never break */ }
  }

  return response
}

/** BUG-2026-05-23-EMAIL-STUCK-LOADING: typed timeout error thrown
 *  when the client-side fetch timeout fires. Distinct from
 *  QuotechApiError (which represents server-side error responses)
 *  AND from native AbortError (which represents caller-driven cancel)
 *  so UI code can render a retry affordance ONLY for genuine timeouts
 *  — not for legitimate filter-change cancels. */
export class QuotechFetchTimeoutError extends Error {
  readonly name = 'QuotechFetchTimeoutError'
  readonly isTimeout = true
  readonly path: string
  readonly timeoutMs: number
  /** Stable user-facing summary. Distinct from `message` (which
   *  carries the debug-friendly path + timeoutMs) so UI surfaces can
   *  render this verbatim without leaking internal paths into the
   *  operator's toast. */
  readonly userMessage = 'Request timed out — please try again.'
  constructor(path: string, timeoutMs: number) {
    // The `message` field is for devtools / Sentry / Recent Issues
    // — operator-facing UI should read `userMessage` instead.
    super(`Request to ${path} timed out after ${timeoutMs}ms`)
    this.path = path
    this.timeoutMs = timeoutMs
  }
}

/** Compose 0..N AbortSignals into a single signal that aborts when
 *  ANY input aborts. Uses the native AbortSignal.any if available
 *  (Chrome 116+, Safari 17.4+, Firefox 124+ — wide enough that all
 *  Quotech operators in 2026 have it) and falls back to a manual
 *  combiner for older runtimes. Undefined/null inputs are skipped.
 *
 *  Exported for direct use by callers that need the same composition
 *  (e.g. the kanban-host fetch wrapper which has the same hung-fetch
 *  failure mode but a different surface). */
export function _composeAbortSignals(
  ...signals: Array<AbortSignal | null | undefined>
): AbortSignal | undefined {
  const real = signals.filter((s): s is AbortSignal => s != null)
  if (real.length === 0) return undefined
  if (real.length === 1) return real[0]
  // AbortSignal.any throws on iterables of non-signals, so we've
  // pre-filtered. typeof check covers SSR + jsdom-without-shim.
  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any
  if (typeof anyFn === 'function') {
    return anyFn(real)
  }
  // Manual fallback. Each input signal's abort fires the combined
  // controller. Listeners are weak-leak safe — they reference the
  // combined controller (which the caller drops when the response
  // settles), and the GC reclaims everything together.
  const combined = new AbortController()
  for (const sig of real) {
    if (sig.aborted) {
      combined.abort()
      return combined.signal
    }
    sig.addEventListener('abort', () => combined.abort(), { once: true })
  }
  return combined.signal
}

/** Call-site reload breaker — INCIDENT 2026-05-30 (Jake's 30× strobe).
 *
 *  The global reload watchdog (`@quotech/auth/installReloadInstrumentation`)
 *  works by reassigning `window.location.reload`. In some embedded /
 *  hardened browsers that property is non-writable, so the wrap silently
 *  fails and reloads bypass the breaker entirely — the identity-mismatch
 *  trigger below strobed 30× with an empty breaker trail because the
 *  breaker was never consulted.
 *
 *  When the watchdog publishes `__quotech_reload_reload_wrapped__ !== true`
 *  we enforce the breaker HERE, at the call site, against the SAME
 *  sessionStorage contract the watchdog + rtrace + the backend
 *  `reload-circuit-breaker` diagnostics ingest all read. Keys/threshold/
 *  window are mirrored from
 *  `packages/auth/src/reload-watchdog/reloadCircuitBreaker.ts` (kept in
 *  lockstep the same way `src/shared/reload-circuit-breaker.ts` is). If
 *  you change the contract there, change it here. */
const _RL_COUNT_KEY = 'quotech_reload_breaker_count'
const _RL_WINDOW_START_KEY = 'quotech_reload_breaker_window_start'
const _RL_TRIPPED_KEY = 'quotech_reload_breaker_tripped'
const _RL_LAST_REASON_KEY = 'quotech_reload_breaker_last_reason'
const _RL_TRAIL_KEY = 'quotech_reload_breaker_trail'
const _RL_THRESHOLD = 3
const _RL_WINDOW_MS = 10_000
const _RL_TRAIL_MAX = 20

/** Converge-aware identity-mismatch reload one-shot — BUG-2026-06-22
 *  (auth-pipeline-loops family, durable cure). The value is the
 *  `${tab}>${server}` identity pair this tab has ALREADY reloaded for in this
 *  session. Rationale: a hard reload only RESOLVES a transient sibling-tab
 *  cookie swap — that converges in exactly one reload. A PERSISTENT
 *  cookie/bearer drift (two simultaneously-valid identities, e.g. an operator
 *  bearer "19" vs a tenant cookie "18") can NEVER converge — every boot
 *  re-asserts both identity authorities — so a second reload only strobes.
 *  We therefore reload AT MOST ONCE per (tab→server) pair and then defer to
 *  the explicit remedy (the poll-driven IdentityMismatchBanner "Switch
 *  Account" / sign out+in). sessionStorage is per-tab and SURVIVES the reload,
 *  so the post-reload boot can see we already tried. The key is shared,
 *  byte-for-byte, with `@quotech/auth` tabIdentity.ts (the axios twin) so the
 *  fetch + axios clients coordinate to a single reload per drift. Distinct
 *  axis from the `_RL_*` strobe breaker above: the breaker bounds reloads/sec
 *  across ALL causes; this bounds reloads to one per DISTINCT drift. */
const _IDENTITY_RELOAD_ONESHOT_KEY = 'quotech_identity_reload_oneshot'

/** True when the global watchdog verified it actually wrapped
 *  `window.location.reload`. `=== true` is deliberate: an undefined flag
 *  (old build / watchdog absent) is treated as "not live" so we still
 *  self-limit. */
function _watchdogReloadWrapped(): boolean {
  if (typeof window === 'undefined') return false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).__quotech_reload_reload_wrapped__ === true
}

/** Minimal mirror of `recordReloadAttempt`. Returns whether the reload
 *  should proceed. Writes the same keys + trail so the watchdog banner,
 *  the rtrace HUD and the backend diagnostic all see a consistent loop
 *  record even when the global wrapper is inactive. Never throws. */
function _recordReloadAttemptLocal(reason: string): { allowed: boolean; count: number } {
  if (typeof sessionStorage === 'undefined') return { allowed: true, count: 1 }
  const now = Date.now()
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  let count = 0
  let windowStart = 0
  let tripped = false
  try {
    count = parseInt(sessionStorage.getItem(_RL_COUNT_KEY) || '0', 10) || 0
    windowStart = parseInt(sessionStorage.getItem(_RL_WINDOW_START_KEY) || '0', 10) || 0
    tripped = sessionStorage.getItem(_RL_TRIPPED_KEY) === '1'
  } catch { /* private mode — fall through with zeros */ }

  let nextCount: number
  let nextWindowStart: number
  let nextTripped: boolean
  let allowed: boolean
  let outcome: 'allowed' | 'suppressed' | 'alreadyTripped'

  if (windowStart === 0 || now - windowStart > _RL_WINDOW_MS) {
    nextCount = 1; nextWindowStart = now; nextTripped = false; allowed = true; outcome = 'allowed'
  } else if (tripped) {
    nextCount = count + 1; nextWindowStart = windowStart; nextTripped = true; allowed = false; outcome = 'alreadyTripped'
  } else {
    nextCount = count + 1; nextWindowStart = windowStart
    if (nextCount > _RL_THRESHOLD) { nextTripped = true; allowed = false; outcome = 'suppressed' }
    else { nextTripped = false; allowed = true; outcome = 'allowed' }
  }

  try {
    sessionStorage.setItem(_RL_COUNT_KEY, String(nextCount))
    sessionStorage.setItem(_RL_WINDOW_START_KEY, String(nextWindowStart))
    if (nextTripped) sessionStorage.setItem(_RL_TRIPPED_KEY, '1')
    else sessionStorage.removeItem(_RL_TRIPPED_KEY)
    sessionStorage.setItem(_RL_LAST_REASON_KEY, reason)
    const raw = sessionStorage.getItem(_RL_TRAIL_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    const trail = Array.isArray(parsed) ? parsed : []
    trail.push({ at: now, pathname, reason, outcome, count: nextCount })
    while (trail.length > _RL_TRAIL_MAX) trail.shift()
    sessionStorage.setItem(_RL_TRAIL_KEY, JSON.stringify(trail))
  } catch { /* ignore */ }

  return { allowed, count: nextCount }
}

/** Local mirror of `@quotech/auth.handleResponseIdentity`. See
 *  function docstring above for why this is inlined. Treats a missing
 *  sessionStorage / missing header as a no-op. */
function _assertResponseIdentityLocal(response: Response, path: string): void {
  if (typeof sessionStorage === 'undefined') return
  const serverRaw = response.headers.get('x-user-id') ?? response.headers.get('X-User-Id')
  if (!serverRaw) return
  const serverId = String(serverRaw).trim().toLowerCase()
  if (!serverId) return
  let tabId: string | null = null
  try { tabId = sessionStorage.getItem('quotech_tab_user_id') } catch { return }
  if (!tabId) {
    // First authenticated response after boot → pin the server's
    // identity. Subsequent comparisons enforce stickiness.
    try { sessionStorage.setItem('quotech_tab_user_id', serverId) } catch { /* noop */ }
    return
  }
  if (tabId === serverId) return
  // Legacy email pin vs numeric pk — migrate silently (see tabIdentity.ts).
  if (tabId.includes('@') && !serverId.includes('@')) {
    try { sessionStorage.setItem('quotech_tab_user_id', serverId) } catch { /* noop */ }
    return
  }
  // BUG-2026-05-22-EVERY-SECOND-RELOAD-LOOP (fifth manifestation):
  //   Mirror the cleanup that tabIdentity.handleResponseIdentity
  //   does in @quotech/auth — clear the legacy localStorage bearer
  //   keys + the sessionStorage tab pin BEFORE scheduling the
  //   reload. Without this, the reload converges in zero steps:
  //   sessionStorage survives reloads, so the post-reload first
  //   response hits the same mismatch and reloads again. Loop.
  //
  //   Symmetric with the @quotech/auth fix; the pin test
  //   `crossTabIdentity.pin.test.ts` enforces both stay in lockstep
  //   when either is updated.
  // Converge-aware reload (BUG-2026-06-22, durable cure for the
  // auth-pipeline-loops identity reload-loop family). Has this exact
  // (tab→server) drift already cost us a reload this session? If so, the
  // reload FAILED to converge it (sessionStorage survives reloads, so a
  // converged drift would never reach here again with the same pair) → it is
  // a persistent two-identity drift and a second reload would only strobe.
  const driftPair = `${tabId}>${serverId}`
  let reloadedThisPair = false
  try {
    reloadedThisPair = sessionStorage.getItem(_IDENTITY_RELOAD_ONESHOT_KEY) === driftPair
  } catch { /* private mode — treat as not-yet-tried */ }
  // Security action (ALWAYS, every mismatch, regardless of whether we reload):
  // drop the stale bearer + tab pin so we stop USING the wrong-identity
  // credential. Data calls then fail closed. Both clears are required to
  // converge a transient swap in a single reload (BUG-2026-05-22).
  try {
    localStorage.removeItem('IdToken')
    localStorage.removeItem('accessTokennew')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refreshToken')
  } catch { /* private mode / quota — ignore */ }
  try {
    sessionStorage.removeItem('quotech_tab_user_id')
  } catch { /* private mode / quota — ignore */ }
  console.warn(
    `[quotechFetch] Response identity mismatch on ${path}. Tab pinned `
    + `to "${tabId}" but server stamped "${serverId}". Cleared stale `
    + 'bearer + tab pin; '
    + (reloadedThisPair
      ? 'a reload already failed to converge this drift — NOT reloading again.'
      : 'reloading.'),
  )
  try {
    const payload = JSON.stringify({
      userId: serverId,
      orgId: null,
      t: Date.now(),
      reason: 'quotechFetch.responseHeaderMismatch',
    })
    localStorage.setItem('quotech:identity_changed', payload)
    localStorage.removeItem('quotech:identity_changed')
  } catch { /* broadcast must never break the response */ }
  if (typeof window !== 'undefined') {
    if (reloadedThisPair) {
      // Persistent, non-converging drift: a full-page reload already failed to
      // reconcile this exact (tab→server) pair this session. Stop — reloading
      // two simultaneously-live identities is futile by design and just
      // strobes. The bearer/pin are already cleared (fail closed); the remedy
      // is the poll-driven IdentityMismatchBanner ("Switch Account") or an
      // explicit sign out + back in, the only fixes for a real cookie/bearer
      // drift.
      console.error(
        `[quotechFetch] persistent identity drift (tab="${tabId}" server="${serverId}"): `
        + 'a full-page reload already failed to reconcile it this session, so NOT '
        + 'reloading again — this is what stops the strobe. Both identities are live; '
        + 'use "Switch Account" on the identity banner, or sign out and back in.',
      )
      return
    }
    // First reload for this pair: record the one-shot BEFORE reloading (it
    // survives the reload, so the post-reload boot knows we already tried) and
    // converge a genuine transient sibling-tab swap in this single reload.
    try { sessionStorage.setItem(_IDENTITY_RELOAD_ONESHOT_KEY, driftPair) } catch { /* private mode — best effort */ }
    // When the global watchdog successfully wrapped window.location.reload
    // it will account for + suppress this reload itself, so just call it.
    // When it could NOT wrap (non-writable in this browser), the breaker
    // is otherwise blind to reloads — so we enforce it HERE so the loop
    // self-limits at the threshold instead of strobing (BUG-2026-05-30).
    if (_watchdogReloadWrapped()) {
      setTimeout(() => { window.location.reload() }, 150)
      return
    }
    const verdict = _recordReloadAttemptLocal(
      `[quotechFetch] identity-mismatch reload (tab="${tabId}" server="${serverId}" path=${path})`,
    )
    if (!verdict.allowed) {
      console.error(
        `[quotechFetch] reload SUPPRESSED by call-site breaker after `
        + `${verdict.count} identity-mismatch reloads in ${_RL_WINDOW_MS / 1000}s. `
        + 'Not reloading — this stops the strobe. If you are stuck, sign out '
        + 'and back in (the server identity differs from this tab\'s pin).',
      )
      return
    }
    setTimeout(() => { window.location.reload() }, 150)
  }
}

export async function quotechFetch(path: string, options: QuotechRequestOptions = {}) {
  const response = await _quotechFetchOnce(path, options)

  // Self-heal the reconnect banner: a 2xx from the master backend
  // while an auth-lost banner is active means the session recovered
  // out-of-band (operator reconnected in a sibling tab, or a transient
  // blip cleared). Auto-hide instead of leaving a stale "signed out"
  // banner up while features load fine (BUG-2026-05-31-RECONNECT-
  // BANNER-STICKY). No-ops unless a banner is actually active.
  if (response.ok) {
    dispatchQuotechAuthRestored()
    return response
  }

  // 401 from the master Django backend means the legacy
  // ``quotech_auth`` cookie is missing or expired. Up until 2026-05-21
  // we surfaced this immediately via the QuotechReconnectBanner — but
  // that's broken UX: 99% of "access cookie expired" cases can be
  // silently fixed by hitting `POST /api/auth/v2/refresh/`, which
  // reads the 30-day SameSite=None HttpOnly `quotech_refresh` cookie
  // and mints a fresh access cookie pair. The user shouldn't see a
  // banner — they should just see the page load.
  //
  // The /api/auth/* endpoints themselves are excluded because (a) a
  // refresh-on-refresh would be a no-op or worse, and (b) a
  // genuinely-bad refresh cookie returning 401 from
  // /api/auth/v2/refresh/ is the ONE place we MUST surface the
  // banner (it's the auth subsystem itself telling us the user is
  // truly signed out).
  if (response.status !== 401 || path.includes('/auth/')) {
    return response
  }

  // Try silent refresh. `attemptSilentRefresh()` is dedup'd at the
  // module level — a single page-load 401 cascade produces ONE
  // refresh round-trip shared across all callers.
  const refreshed = await attemptSilentRefresh()
  if (refreshed) {
    const retry = await _quotechFetchOnce(path, options)
    // If the retry STILL 401s, the refresh round-trip reported 200
    // but the cookies it set aren't being honoured by the BE (clock
    // skew, partial outage, cookie domain mismatch, etc.). Fall
    // through to the banner so the operator has a real recovery
    // path — silent retry has done all it can.
    if (retry.status !== 401) return retry
  }

  // Either the refresh round-trip failed (refresh cookie also dead
  // → user genuinely needs to re-login) or the retry still 401'd
  // (broken cookie acceptance). Either way, show the banner so the
  // operator can take action.
  maybeDispatchAuthLost(path)
  return response
}

/**
 * QuotechApiError — typed error thrown from quotechJson.
 *
 * The plain ``Error.message`` we used to throw served a UI that
 * toasted ``error.message`` verbatim. When the master backend
 * returned an HTML 500 page (raw Django error) the toast would
 * literally render ``<!doctype html>...`` — see the
 * 2026-05-04 incident. Now we throw this richer type so:
 *
 *   - UI code can switch on ``status`` and ``code`` (e.g. show a
 *     specific banner for 429 capacity rejections instead of a
 *     generic toast)
 *   - The user-facing ``message`` is always sanitised
 *     (no raw HTML)
 *   - The original body is preserved on ``rawBody`` for the
 *     Recent Issues panel / dev console
 */
export class QuotechApiError extends Error {
  status: number
  code?: string
  retryAfterSeconds?: number
  rawBody?: string
  details?: Record<string, unknown>

  constructor(args: {
    message: string
    status: number
    code?: string
    retryAfterSeconds?: number
    rawBody?: string
    details?: Record<string, unknown>
  }) {
    super(args.message)
    this.name = 'QuotechApiError'
    this.status = args.status
    this.code = args.code
    this.retryAfterSeconds = args.retryAfterSeconds
    this.rawBody = args.rawBody
    this.details = args.details
  }
}

/**
 * Detect HTML content masquerading as a JSON response.
 *
 * Django's default 500 handler returns text/html; old proxies /
 * load balancers (cloudfront, ALB) sometimes return HTML for
 * 502 / 503 / 504. In all of these cases the body starts with
 * ``<!DOCTYPE`` or ``<html``, often with whitespace or a BOM
 * before. We use a cheap startsWith check on the trimmed body
 * (~5 us) — costs less than logging the previous behaviour
 * dumped to the console.
 */
function looksLikeHtml(body: string | null | undefined): boolean {
  if (!body) return false
  const trimmed = body.trimStart().slice(0, 16).toLowerCase()
  return (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<?xml')
  )
}

/**
 * Build a user-facing error message that NEVER contains raw
 * server output. Operators can dig into the actual body via the
 * Recent Issues panel (which receives the QuotechApiError with
 * rawBody attached) or the browser dev console.
 */
function userFacingMessage(args: {
  status: number
  parsedDetail: string
  rawText: string
}): string {
  const { status, parsedDetail, rawText } = args
  if (parsedDetail && !looksLikeHtml(parsedDetail)) {
    return parsedDetail
  }
  if (status === 429) {
    return 'System is busy. Please try again shortly.'
  }
  if (status === 401) {
    return 'Your session has expired. Please reconnect.'
  }
  if (status === 403) {
    return 'You do not have permission to perform that action.'
  }
  if (status === 404) {
    return 'The requested resource was not found.'
  }
  if (status >= 500 || looksLikeHtml(rawText)) {
    return 'Server error — see Recent issues for details.'
  }
  return `Request failed with status ${status}.`
}

export async function quotechJson<T>(path: string, options: QuotechRequestOptions = {}) {
  const response = await quotechFetch(path, options)
  const text = await response.text()
  let data: T | null = null

  if (text) {
    try {
      data = JSON.parse(text) as T
    } catch {
      data = null
    }
  }

  if (import.meta.env.DEV && path.includes('/emails')) {
    console.debug('[quotechJson]', path, {
      status: response.status,
      ok: response.ok,
      bodyLength: text.length,
      bodyPreview: text.slice(0, 300),
      parsedType: data === null ? 'null' : typeof data,
      parsedIsArray: Array.isArray(data),
      parsedKeys: data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data) : undefined,
    })
  }

  if (!response.ok) {
    // Pull a structured detail from the parsed body if present.
    // Note: do NOT fall through to raw `text` here — a Django
    // HTML 500 page would surface verbatim and the UI would
    // toast `<!doctype html>...` (the 2026-05-04 bug).
    const parsedDetail =
      (data && typeof data === 'object' && (
        'error' in data ? String((data as Record<string, unknown>).error ?? '') :
        'detail' in data ? String((data as Record<string, unknown>).detail ?? '') :
        'message' in data ? String((data as Record<string, unknown>).message ?? '') :
        ''
      )) || ''

    // Capacity guard 429s carry retry_after_seconds in the body
    // and a Retry-After header. Surface both on the typed error
    // so callers can render a "try again in N seconds" toast
    // instead of a generic 'too many requests'.
    const retryAfterFromHeader = parseInt(
      response.headers.get('Retry-After') || '0',
      10,
    ) || undefined
    const retryAfterFromBody = (
      data && typeof data === 'object' && 'retry_after_seconds' in data
        ? Number((data as Record<string, unknown>).retry_after_seconds)
        : undefined
    )
    const retryAfterSeconds =
      Number.isFinite(retryAfterFromBody as number)
        ? (retryAfterFromBody as number)
        : retryAfterFromHeader

    // Prefer an explicit machine ``code`` from the body; fall back to
    // ``reason`` for the many endpoints that only send the latter.
    // The capacity-guard 429 sends BOTH — ``code: 'capacity_rejected'``
    // (the stable value EmailLayout/useCapacityPause branch on) plus a
    // granular ``reason`` ('hard_limit_tripped'/'cooldown') for humans.
    // Reading only ``reason`` here meant the FE saw 'hard_limit_tripped'
    // and the graceful capacity-pause branch never fired, so every shed
    // surfaced as a hard "Email sync failed" error. Preferring ``code``
    // fixes that while staying backward-compatible (reason fallback).
    const code = (data && typeof data === 'object'
      ? (('code' in data && (data as Record<string, unknown>).code
          ? String((data as Record<string, unknown>).code)
          : ('reason' in data
              ? String((data as Record<string, unknown>).reason ?? '')
              : undefined)))
      : undefined) || undefined

    throw new QuotechApiError({
      message: userFacingMessage({
        status: response.status,
        parsedDetail,
        rawText: text,
      }),
      status: response.status,
      code,
      retryAfterSeconds,
      rawBody: text,
      details: (data && typeof data === 'object'
        ? (data as Record<string, unknown>)
        : undefined),
    })
  }

  return {
    response,
    data: data as T,
  }
}

export function getQuotechBackendUrl() {
  return QUOTECH_BACKEND
}
